import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

/* ── DropdownMenu Context ────────────────────────────────────────────── */

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue | null>(null);

function useDropdownMenuContext() {
  const ctx = React.useContext(DropdownMenuContext);
  if (!ctx) throw new Error("DropdownMenu components must be used within <DropdownMenu>");
  return ctx;
}

/* ── DropdownMenu Root ───────────────────────────────────────────────── */

export interface DropdownMenuProps {
  children: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Dropdown menu root - manages open/close state. */
function DropdownMenu({ children, open: controlledOpen, defaultOpen = false, onOpenChange }: DropdownMenuProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const setOpen = React.useCallback(
    (value: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [controlledOpen, onOpenChange]
  );

  // Close on outside click (use "click" so menu items receive full click before close)
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-dropdown-content]") && !target.closest("[data-dropdown-trigger]")) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [open, setOpen]);

  // Close on Escape
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, setOpen]);

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen, triggerRef }}>
      <div className="relative inline-block">{children}</div>
    </DropdownMenuContext.Provider>
  );
}

/* ── DropdownMenuTrigger ─────────────────────────────────────────────── */

export interface DropdownMenuTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** When true, uses the single child element as the trigger instead of rendering a <button>. */
  asChild?: boolean;
}

const DropdownMenuTrigger = React.forwardRef<HTMLButtonElement, DropdownMenuTriggerProps>(
  ({ className, onClick, asChild = false, children, ...props }, forwardedRef) => {
    const { open, setOpen, triggerRef } = useDropdownMenuContext();

    const setRefs = (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    };

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      setOpen(!open);
      onClick?.(e);
    };

    if (asChild && React.isValidElement(children)) {
      const childProps = (children.props ?? {}) as Record<string, unknown>;
      const childOnClick = childProps.onClick as
        | React.MouseEventHandler<HTMLButtonElement>
        | undefined;

      return React.cloneElement(
        children as React.ReactElement<Record<string, unknown>>,
        {
          ...props,
          "data-dropdown-trigger": true,
          "aria-expanded": open,
          "aria-haspopup": "menu",
          type: (childProps.type as string | undefined) ?? "button",
          className: cn(childProps.className as string | undefined, className),
          onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
            setOpen(!open);
            childOnClick?.(e);
            onClick?.(e);
          },
          ref: setRefs,
        },
      );
    }

    return (
      <button
        ref={setRefs}
        data-dropdown-trigger
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={handleClick}
        className={cn(className)}
        {...props}
      >
        {children}
      </button>
    );
  },
);
DropdownMenuTrigger.displayName = "DropdownMenuTrigger";

/* ── DropdownMenuContent ─────────────────────────────────────────────── */

export interface DropdownMenuContentProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Align the dropdown relative to the trigger. */
  align?: "start" | "end";
}

const DropdownMenuContent = React.forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  ({ className, align = "end", style, onKeyDown: onKeyDownProp, ...props }, ref) => {
    const { open, triggerRef } = useDropdownMenuContext();
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = React.useState({ top: 0, left: 0 });

    const setRefs = (node: HTMLDivElement | null) => {
      contentRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    };

    React.useLayoutEffect(() => {
      if (!open || !triggerRef.current) return;

      const update = () => {
        const trigger = triggerRef.current!;
        const rect = trigger.getBoundingClientRect();
        const contentWidth = contentRef.current?.offsetWidth ?? 128;
        const contentHeight = contentRef.current?.offsetHeight ?? 0;
        const gap = 8;
        const margin = 8;

        const fitsBelow = rect.bottom + gap + contentHeight <= window.innerHeight - margin;
        const top = fitsBelow || rect.top - gap - contentHeight < margin
          ? rect.bottom + gap
          : rect.top - gap - contentHeight;
        const rawLeft = align === "end" ? rect.right - contentWidth : rect.left;
        const left = Math.min(Math.max(margin, rawLeft), window.innerWidth - contentWidth - margin);

        setPosition({ top, left });
      };

      update();
      const raf = requestAnimationFrame(update);
      window.addEventListener("scroll", update, true);
      window.addEventListener("resize", update);
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("scroll", update, true);
        window.removeEventListener("resize", update);
      };
    }, [open, triggerRef, align]);

    if (!open) return null;

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDownProp?.(e);
      const items = Array.from(
        contentRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([data-disabled])') ?? [],
      );
      if (items.length === 0) return;
      const index = items.indexOf(document.activeElement as HTMLElement);
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const step = e.key === "ArrowDown" ? 1 : -1;
        items[(index + step + items.length) % items.length]?.focus();
      } else if ((e.key === "Enter" || e.key === " ") && index >= 0) {
        e.preventDefault();
        items[index]?.click();
      }
    };

    return createPortal(
      <div
        ref={setRefs}
        data-dropdown-content
        role="menu"
        onKeyDown={onKeyDown}
        style={{
          position: "fixed",
          top: position.top,
          left: position.left,
          zIndex: 9999,
          ...style,
        }}
        className={cn(
          "menu-surface min-w-40 overflow-hidden p-1.5",
          className,
        )}
        {...props}
      />,
      document.body,
    );
  },
);
DropdownMenuContent.displayName = "DropdownMenuContent";

/* ── DropdownMenuItem ────────────────────────────────────────────────── */

export interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
  inset?: boolean;
}

const DropdownMenuItem = React.forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  ({ className, disabled, inset, onClick, ...props }, ref) => {
    const { setOpen } = useDropdownMenuContext();

    return (
      <div
        ref={ref}
        role="menuitem"
        tabIndex={disabled ? -1 : 0}
        data-disabled={disabled || undefined}
        onClick={(e) => {
          if (disabled) return;
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
          // Defer so the portal can unmount before download / print handlers run
          window.setTimeout(() => onClick?.(e), 0);
        }}
        className={cn(
          "relative flex cursor-pointer select-none items-center rounded-xl px-3 py-2 text-sm text-popover-foreground outline-none transition-colors",
          "hover:bg-muted focus:bg-muted focus:text-foreground",
          "[&_svg]:text-muted-foreground hover:[&_svg]:text-foreground",
          disabled && "cursor-not-allowed opacity-40",
          inset && "pl-8",
          className
        )}
        {...props}
      />
    );
  }
);
DropdownMenuItem.displayName = "DropdownMenuItem";

/* ── DropdownMenuSeparator ───────────────────────────────────────────── */

const DropdownMenuSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      role="separator"
      className={cn("-mx-1.5 my-1.5 h-px bg-border/70", className)}
      {...props}
    />
  )
);
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

/* ── DropdownMenuLabel ───────────────────────────────────────────────── */

export interface DropdownMenuLabelProps extends React.HTMLAttributes<HTMLDivElement> {
  inset?: boolean;
}

const DropdownMenuLabel = React.forwardRef<HTMLDivElement, DropdownMenuLabelProps>(
  ({ className, inset, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "px-3 py-1.5 text-xs font-semibold text-muted-foreground",
        inset && "pl-8",
        className
      )}
      {...props}
    />
  )
);
DropdownMenuLabel.displayName = "DropdownMenuLabel";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
};
